import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX, SkipBack, SkipForward, Settings, X, Minimize } from 'lucide-react';
import { Button } from './ui/button';

// CSS for volume slider
const sliderStyles = `
  .volume-slider {
    -webkit-appearance: none;
    appearance: none;
    outline: none;
    cursor: pointer;
  }
  .volume-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    height: 12px;
    width: 12px;
    background: #ef4444;
    border-radius: 50%;
    cursor: pointer;
  }
  .volume-slider::-moz-range-thumb {
    height: 12px;
    width: 12px;
    background: #ef4444;
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
`;

const FullscreenVideoPlayer = ({ movie, isOpen, onClose, videos, setCurrentVideo }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(100);
  const [showControls, setShowControls] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const playerRef = useRef(null);
  const timeUpdateIntervalRef = useRef(null);

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeVideoId(movie?.trailerUrl);

  // Load YouTube API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      
      window.onYouTubeIframeAPIReady = () => {
        initializePlayer();
      };
    } else if (isOpen && videoId) {
      initializePlayer();
    }
    
    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
    };
  }, [isOpen, videoId]);

  const initializePlayer = () => {
    if (!window.YT || !videoId || !isOpen) return;
    
    if (playerRef.current) {
      playerRef.current.destroy();
    }
    
    playerRef.current = new window.YT.Player('youtube-player', {
      height: '100%',
      width: '100%',
      videoId: videoId,
      playerVars: {
        autoplay: 1,
        controls: 0,
        disablekb: 1,
        fs: 0,
        modestbranding: 1,
        rel: 0,
        showinfo: 0,
        iv_load_policy: 3,
        playsinline: 1
      },
      events: {
        onReady: handlePlayerReady,
        onStateChange: (event) => {
          handlePlayerStateChange(event);
          if (event.data === window.YT.PlayerState.ENDED) {
            handleVideoEnd(videos, movie);
          }
        },
        onError: handlePlayerError
      }
    });
  };

  const handlePlayerReady = (event) => {
    setIsLoading(false);
    const player = event.target;
    setDuration(player.getDuration());
    setVolume(player.getVolume());
    setIsMuted(player.isMuted());
    
    // Start time tracking
    timeUpdateIntervalRef.current = setInterval(() => {
      if (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
        setCurrentTime(playerRef.current.getCurrentTime());
      }
    }, 1000);
  };

  const handlePlayerStateChange = (event) => {
    const state = event.data;
    if (state === window.YT.PlayerState.PLAYING) {
      setIsPlaying(true);
    } else if (state === window.YT.PlayerState.PAUSED || state === window.YT.PlayerState.ENDED) {
      setIsPlaying(false);
    }
  };

  const handlePlayerError = (event) => {
    console.error('YouTube Player Error:', event.data);
    setIsLoading(false);
  };

  // Control functions
  const togglePlayPause = () => {
    if (!playerRef.current) return;
    
    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  };

  const skipBack = () => {
    if (!playerRef.current) return;
    const currentTime = playerRef.current.getCurrentTime();
    const newTime = Math.max(0, currentTime - 10);
    playerRef.current.seekTo(newTime, true);
    setCurrentTime(newTime);
  };

  const skipForward = () => {
    if (!playerRef.current) return;
    const currentTime = playerRef.current.getCurrentTime();
    const duration = playerRef.current.getDuration();
    const newTime = Math.min(duration, currentTime + 10);
    playerRef.current.seekTo(newTime, true);
    setCurrentTime(newTime);
  };

  const toggleMute = () => {
    if (!playerRef.current) return;
    
    if (isMuted) {
      playerRef.current.unMute();
    } else {
      playerRef.current.mute();
    }
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (newVolume) => {
    if (!playerRef.current) return;
    playerRef.current.setVolume(newVolume);
    setVolume(newVolume);
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  const handleVideoClick = () => {
    togglePlayPause();
  };

  useEffect(() => {
    if (isOpen) {
      // Hide cursor and enter fullscreen-like experience
      document.body.style.overflow = 'hidden';
      document.body.style.cursor = 'none';
      setIsClosing(false);
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.cursor = 'auto';
      
      // Cleanup player when closing
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
    }

    return () => {
      document.body.style.overflow = 'auto';
      document.body.style.cursor = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      document.body.style.cursor = 'auto';
      
      // Clear existing timeout
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      
      // Set new timeout to hide controls
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
        document.body.style.cursor = 'none';
      }, 3000);
    };

    const handleKeyPress = (e) => {
      switch (e.key) {
        case 'Escape':
          handleClose();
          break;
        case ' ':
          e.preventDefault();
          togglePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skipBack();
          break;
        case 'ArrowRight':
          e.preventDefault();
          skipForward();
          break;
        case 'm':
        case 'M':
          toggleMute();
          break;
        default:
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('keydown', handleKeyPress);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isOpen, isPlaying, isMuted]);

  const handleVideoLoad = () => {
    setIsLoading(false);
    setDuration(movie?.runtime * 60 || 7200); // Convert minutes to seconds, default 2 hours
  };

  const handleClose = () => {
    setIsClosing(true);
    // Cleanup player before closing
    if (playerRef.current) {
      playerRef.current.pauseVideo();
    }
    // Add a small delay for smooth closing animation
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e) => {
    if (!playerRef.current) return;
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    const newTime = clickPosition * duration;
    
    playerRef.current.seekTo(newTime, true);
    setCurrentTime(newTime);
  };

  const handleVideoEnd = (videos, movie) => {
    if (!videos || !Array.isArray(videos) || !movie) {
      console.error('Videos list or movie metadata is undefined. Cannot proceed with handleVideoEnd.');
      onClose();
      return;
    }

    const currentIndex = videos.findIndex(video => video.videoId === movie.videoId);
    if (currentIndex !== -1 && currentIndex < videos.length - 1) {
      const nextVideo = videos[currentIndex + 1];
      setCurrentVideo(nextVideo);
    } else {
      onClose();
    }
  };

  // useEffect(() => {
  //   console.log('FullscreenVideoPlayer props:', { videos, movie });
  // }, [videos, movie]);

  if (!isOpen || !movie) return null;

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div>
      {/* Inject CSS styles */}
      <style>{sliderStyles}</style>
      
      <div className={`fixed inset-0 z-[9999] bg-black transition-opacity duration-200 ${isClosing ? 'opacity-0' : 'opacity-100'}`}>
      {/* Video Container */}
      <div className="relative w-full h-full flex items-center justify-center">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-white text-xl">Loading...</div>
          </div>
        )}
        
        {/* YouTube Embed for demonstration (in production, this would be actual video) */}
        {videoId ? (
          <div 
            id="youtube-player" 
            className="w-full h-full cursor-pointer"
            onClick={handleVideoClick}
          />
        ) : (
          <div 
            className="w-full h-full bg-gradient-to-br from-gray-900 to-black flex items-center justify-center cursor-pointer"
            onClick={handleVideoClick}
          >
            <div className="text-center">
              <h2 className="text-4xl font-bold text-white mb-4">{movie.title || movie.name}</h2>
              <p className="text-gray-300 text-lg mb-8">Video content would play here</p>
              <p className="text-gray-400">Demo with YouTube trailer - Full movie playback would be implemented with actual streaming service</p>
            </div>
          </div>
        )}

        {/* Controls Overlay */}
        <div 
          className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30 transition-opacity duration-300 ${
            showControls ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Top Controls */}
          <div id="top-control" className="absolute top-0 left-0 right-0 p-6 flex items-center justify-between bg-gradient-to-b from-black/80 via-black/60 to-transparent" style={{ height: '120px', paddingTop: '20px' }}>
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                className="p-2 rounded-full text-white hover:bg-white/20"
                onClick={handleClose}
              >
                <X className="w-6 h-6" />
              </Button>
              <div className="text-white">
                <h3 className="text-lg font-semibold">{movie.title || movie.name}</h3>
                <p className="text-sm text-gray-300">
                  {new Date(movie.release_date || movie.first_air_date).getFullYear()} • 
                  {movie.runtime && ` ${movie.runtime}m`} • HD
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                className="p-2 rounded-full text-white hover:bg-white/20"
              >
                <Settings className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="p-2 rounded-full text-white hover:bg-white/20"
                onClick={handleClose}
              >
                <Minimize className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Center Play/Pause Button */}
          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Button
                variant="ghost"
                size="lg"
                className="p-6 rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 pointer-events-auto"
                onClick={togglePlayPause}
              >
                <Play className="w-16 h-16 fill-current" />
              </Button>
            </div>
          )}

          {/* Bottom Controls */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            {/* Progress Bar */}
            <div className="mb-4">
              <div 
                className="w-full h-1 bg-gray-600 rounded-full cursor-pointer hover:h-2 transition-all"
                onClick={handleProgressClick}
              >
                <div 
                  className="h-full bg-red-600 rounded-full transition-all"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2 rounded-full text-white hover:bg-white/20"
                  onClick={togglePlayPause}
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6" />
                  ) : (
                    <Play className="w-6 h-6 fill-current" />
                  )}
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2 rounded-full text-white hover:bg-white/20"
                  onClick={skipBack}
                >
                  <SkipBack className="w-5 h-5" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2 rounded-full text-white hover:bg-white/20"
                  onClick={skipForward}
                >
                  <SkipForward className="w-5 h-5" />
                </Button>
                
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-2 rounded-full text-white hover:bg-white/20"
                    onClick={toggleMute}
                    onMouseEnter={() => setShowVolumeSlider(true)}
                    onMouseLeave={() => setShowVolumeSlider(false)}
                  >
                    {isMuted || volume === 0 ? (
                      <VolumeX className="w-5 h-5" />
                    ) : (
                      <Volume2 className="w-5 h-5" />
                    )}
                  </Button>
                  
                  {/* Volume Slider */}
                  {showVolumeSlider && (
                    <div 
                      className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-0 bg-black/80 backdrop-blur-sm rounded-lg p-2"
                      onMouseEnter={() => setShowVolumeSlider(true)}
                      onMouseLeave={() => setShowVolumeSlider(false)}
                    >
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={volume}
                        onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
                        className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer volume-slider"
                        style={{
                          background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${volume}%, #4b5563 ${volume}%, #4b5563 100%)`
                        }}
                      />
                    </div>
                  )}
                </div>
                
                <div className="text-white text-sm">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 px-4 py-2 text-sm"
                >
                  Audio & Subtitles
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2 rounded-full text-white hover:bg-white/20"
                >
                  <Settings className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Transparent Click Overlay */}
        <div
          className="absolute inset-0 z-10 cursor-pointer"
          style={{ top: '120px', bottom: '120px' }}
          onClick={handleVideoClick}
        ></div>
      </div>
      </div>
    </div>
  );
};

FullscreenVideoPlayer.defaultProps = {
  videos: [],
  movie: null,
};

export default FullscreenVideoPlayer;
